import { useMemo, useState } from "react";
import { Plus, X, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { useSeason, GIFT_BOX_IMG } from "@/lib/holiday";
import { useProducts, money, cleanTitle } from "@/lib/api";
import { useCart, toLine } from "@/lib/cart";
import { SplitText } from "@/components/fx/Motion";

const MAX = 5;

const Slot = ({ p, i, onRemove, t }) => (
  <div data-testid={`gift-box-slot-${i}`} className="aspect-square border border-dashed border-[#c9a55c]/40 bg-[#0b0a08]/60 relative flex items-center justify-center overflow-hidden">
    <AnimatePresence mode="popLayout">
      {p ? (
        <motion.div key={p.handle} className="absolute inset-0 bg-white" initial={{ y: -80, opacity: 0, rotate: -10 }} animate={{ y: 0, opacity: 1, rotate: 0 }} exit={{ scale: 0, opacity: 0 }} transition={{ type: "spring", stiffness: 200, damping: 18 }}>
          <img src={p.images[0]} alt={p.title} className="h-full w-full object-contain p-2" />
          <button data-testid={`gift-box-remove-${i}`} onClick={onRemove} aria-label={t("box_remove")} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-[#0b0a08] text-white flex items-center justify-center"><X size={12} /></button>
        </motion.div>
      ) : <span className="text-[10px] text-white/30 text-center px-1">{i + 1}</span>}
    </AnimatePresence>
  </div>
);

const Summary = ({ picked, tier, next, t, onReset }) => {
  const { add, checkout } = useCart();
  const total = picked.reduce((s, p) => s + p.price, 0);
  const final = tier ? total * (1 - tier.pct / 100) : total;
  const lines = picked.map((p) => toLine(p, p.variants[0]));
  const addAll = () => { lines.forEach((l, i) => add(l, i === lines.length - 1, i === lines.length - 1)); onReset(); };
  return (
    <div className="mt-6 space-y-3">
      <p data-testid="gift-box-progress" className="text-sm text-[#e3cb94]">
        {next ? t("box_progress", { n: next.size - picked.length, pct: next.pct }) : tier ? t("box_full") : ""}
        {tier && next && <span className="block text-gold font-semibold">{t("box_unlocked", { pct: tier.pct })}</span>}
        {tier && !next && <span className="block text-gold font-semibold">{t("box_unlocked", { pct: tier.pct })}</span>}
      </p>
      <div className="flex items-baseline justify-between">
        <span className="text-white/60 text-sm">{t("box_total")}</span>
        <span><span data-testid="gift-box-total" className="text-2xl font-semibold">{money(final)}</span>{tier && <span className="line-through text-white/40 text-sm ml-2">{money(total)}</span>}</span>
      </div>
      {tier && <p className="text-[11px] text-white/45">{t("box_code_note", { code: tier.code })}</p>}
      <button data-testid="gift-box-buy-button" disabled={!picked.length} onClick={() => checkout(lines, tier?.code)} className="btn-gold btn-shine h-14 w-full disabled:opacity-40">{t("box_buy")}</button>
      <button data-testid="gift-box-add-button" disabled={!picked.length} onClick={addAll} className="btn-ghost h-12 w-full disabled:opacity-40">{t("box_add")}</button>
      {!!picked.length && <button data-testid="gift-box-reset" onClick={onReset} className="w-full text-xs text-white/40 hover:text-white">{t("box_reset")}</button>}
    </div>
  );
};

export const GiftBoxBuilder = () => {
  const { t } = useI18n();
  const { tiers, tierFor } = useSeason();
  const { data = [] } = useProducts({});
  const pool = useMemo(() => data.filter((p) => p.available && p.variants.length === 1 && p.price < 150), [data]);
  const [picked, setPicked] = useState([]);
  const tier = tierFor(picked.length);
  const next = tiers.find((x) => x.size > picked.length);
  const toggle = (p) => setPicked((cur) => (cur.some((x) => x.handle === p.handle) ? cur.filter((x) => x.handle !== p.handle) : cur.length < MAX ? [...cur, p] : cur));

  return (
    <section id="box" data-testid="gift-box-section" className="relative py-24 bg-[#0e0d0b] border-y border-white/5 scroll-mt-28 grain">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <p className="eyebrow mb-4">{t("box_eyebrow")}</p>
        <h2 className="font-display text-3xl lg:text-5xl"><SplitText text={t("box_title")} /></h2>
        <p className="text-white/60 mt-4 mb-12 max-w-lg">{t("box_text")}</p>
        <div className="grid lg:grid-cols-[1fr_400px] gap-10 items-start">
          <div>
            <p className="eyebrow mb-4">{t("box_pick")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              {pool.map((p) => {
                const on = picked.some((x) => x.handle === p.handle);
                return (
                  <motion.button key={p.id} data-testid={`gift-box-pick-${p.handle}`} onClick={() => toggle(p)} whileTap={{ scale: 0.95 }} whileHover={{ y: -4 }}
                    disabled={!on && picked.length >= MAX} className={`relative text-left border p-2 transition-colors disabled:opacity-40 ${on ? "border-[#c9a55c] bg-[#c9a55c]/10" : "border-white/10 hover:border-white/30"}`}>
                    <span className={`absolute top-2 right-2 z-10 h-6 w-6 flex items-center justify-center ${on ? "bg-gold text-[#0b0a08]" : "bg-[#0b0a08]/80 text-white"}`}>{on ? <Check size={13} /> : <Plus size={13} />}</span>
                    <div className="aspect-square bg-white"><img src={p.images[0]} alt="" loading="lazy" className="h-full w-full object-contain p-2" /></div>
                    <p className="text-xs mt-2 line-clamp-2 min-h-[2.5em]">{cleanTitle(p.title)}</p>
                    <p className="text-xs text-white/60">{money(p.price)}</p>
                  </motion.button>
                );
              })}
            </div>
          </div>
          <div className="lg:sticky lg:top-28 border border-[#c9a55c]/30 bg-[#0b0a08] p-6">
            <div className="relative aspect-[16/9] overflow-hidden mb-5"><img src={GIFT_BOX_IMG} alt="" className="h-full w-full object-cover" /></div>
            <div className="flex gap-2 mb-4">
              {tiers.map((x) => (
                <span key={x.size} data-testid={`gift-box-tier-${x.size}`} className={`flex-1 text-center text-[11px] py-2 border transition-colors ${picked.length >= x.size ? "bg-gold text-[#0b0a08] border-transparent font-bold" : "border-white/15 text-white/60"}`}>
                  {x.size} · -{x.pct}%
                </span>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-2">
              {Array.from({ length: MAX }).map((_, i) => <Slot key={i} i={i} p={picked[i]} t={t} onRemove={() => setPicked(picked.filter((_, j) => j !== i))} />)}
            </div>
            <Summary picked={picked} tier={tier} next={next} t={t} onReset={() => setPicked([])} />
          </div>
        </div>
      </div>
    </section>
  );
};
